import React, { useState, useEffect, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot } from 'firebase/firestore';

// --- Firebase Configuration ---
// IMPORTANT: Replace with your actual Firebase project configuration.
const firebaseConfig = {
  apiKey: "AIzaSyBs4S7YURrH31JNcgG95PPn2WE38qHsCsk",
  authDomain: "leen-vidyalaya.firebaseapp.com",
  projectId: "leen-vidyalaya",
  storageBucket: "leen-vidyalaya.firebasestorage.app",
  messagingSenderId: "105923188499",
  appId: "1:105923188499:web:3e285bb05936681665ace6",
  measurementId: "G-16HRM54BYF"
};

// --- Initialize Firebase ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- Scroll Fade-in ---
const FadeSection = ({ children }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return <div ref={ref} className={`fade-section${visible ? ' visible' : ''}`}>{children}</div>;
};

// --- Scroll To Top ---
const ScrollToTop = () => {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 400);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    if (!visible) return null;
    return (
        <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-20 right-4 z-40 bg-[var(--color-primary)] text-white w-11 h-11 rounded-full shadow-lg flex items-center justify-center hover:brightness-90 transition-all duration-200"
            aria-label="Back to top"
        >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
        </button>
    );
};

// --- Push Notification Helpers ---
const VAPID_PUBLIC_KEY = 'BKc7K9HNMo5PUFWKlfyWb_Ylj-eVp0xjV4MPtvas0mzHotnBTszLBTnpdeWWREN0YwmIytJmmHxd0_limrwJoL4';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
}

async function registerAndSubscribe() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) return existing;
        return await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
    } catch (err) {
        console.error('SW/Push registration error:', err);
        return null;
    }
}

async function updateSubscriptionClass(selectedClass) {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const subscription = await reg.pushManager.getSubscription();
        if (!subscription) return;
        await fetch('/api/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscription, selectedClass }),
        });
    } catch (_) {}
}

// --- Helper Components ---
const setMeta = (selector, attr, value) => {
    let el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
};

const SEO = ({ title, description }) => {
    useEffect(() => {
        document.title = title;
        setMeta('meta[name="description"]', 'content', description);
        setMeta('meta[property="og:title"]', 'content', title);
        setMeta('meta[property="og:description"]', 'content', description);
        setMeta('meta[name="twitter:title"]', 'content', title);
        setMeta('meta[name="twitter:description"]', 'content', description);
    }, [title, description]);
    return null;
};

const FaviconSetter = ({ faviconUrl }) => {
    useEffect(() => {
        if (faviconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = faviconUrl;

            // Also update apple-touch-icon so installed PWAs / Safari pick up the new icon.
            let appleLink = document.querySelector("link[rel='apple-touch-icon']");
            if (!appleLink) {
                appleLink = document.createElement('link');
                appleLink.rel = 'apple-touch-icon';
                document.head.appendChild(appleLink);
            }
            appleLink.href = faviconUrl;
        }
    }, [faviconUrl]);

    return null;
};

const ThemeSetter = ({ theme = {} }) => {
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--color-primary', theme.primaryColor || '#1e40af');
        root.style.setProperty('--color-secondary', theme.secondaryColor || '#f59e0b');
    }, [theme]);

    return null;
};


// --- Main App Component ---
function MainApp() {
    const [page, setPage] = useState('home');
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [content, setContent] = useState({});
    const [contentLoading, setContentLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (authLoading) return;
        setContentLoading(true);
        const docRef = doc(db, "websiteContent", "main");
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setContent(docSnap.data());
            } else {
                setContent({
                    general: {}, hero: {}, about: {}, programs: {}, contact: {}, news: { items: [] }, gallery: { images: [] }, announcements: [], notices: { common: [] }, homework: {}
                });
            }
            setContentLoading(false);
        }, (error) => {
            console.error("Error fetching content:", error);
            setContentLoading(false);
        });
        return () => unsubscribe();
    }, [authLoading]);

    const navigateTo = (path) => {
        setPage(path);
        window.history.pushState({}, '', `#${path}`);
    };
    
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.replace('#', '');
            if(hash) setPage(hash);
            else setPage('home');
        };
        window.addEventListener('hashchange', handleHashChange);
        handleHashChange();
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    if (authLoading || contentLoading) {
        return <div className="flex justify-center items-center h-screen bg-blue-100"><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div></div>;
    }
    
    const renderPage = () => {
        if (page.startsWith('cms')) {
            if (user) {
                const cmsPage = page.split('/')[1] || 'dashboard';
                switch (cmsPage) {
                    case 'dashboard': return <CMSDashboard navigate={navigateTo} />;
                    case 'general': return <CMSEdit sectionId="general" fields={['logoUrl', 'faviconUrl', 'primaryColor', 'secondaryColor', 'whatsappNumber', 'facebookUrl', 'instagramUrl', 'youtubeUrl']} title="General Site Settings" navigate={navigateTo}/>; 
                    case 'hero': return <CMSEdit sectionId="hero" fields={['heading', 'subheading', 'buttonText', 'imageUrl']} title="Hero Section" navigate={navigateTo}/>;
                    case 'about': return <CMSEdit sectionId="about" fields={['heading', 'content', 'imageUrl', 'achievement1Title', 'achievement1Desc', 'achievement2Title', 'achievement2Desc', 'achievement3Title', 'achievement3Desc']} title="About Us Section" navigate={navigateTo}/>;
                    case 'programs': return <CMSEdit sectionId="programs" fields={['heading', 'program1Title', 'program1Desc', 'program2Title', 'program2Desc', 'program3Title', 'program3Desc']} title="Programs Section" navigate={navigateTo}/>;
                    case 'contact': return <CMSEdit sectionId="contact" fields={['heading', 'address', 'phone', 'email']} title="Contact Section Info" navigate={navigateTo}/>; 
                    case 'news': return <CMSNewsTickerEdit navigate={navigateTo} />;
                    case 'gallery': return <CMSGalleryEdit navigate={navigateTo} />;
                    case 'announcements': return <CMSAnnouncementsEdit navigate={navigateTo} />;
                    case 'notices': return <CMSNoticesEdit navigate={navigateTo} />;
                    case 'homework': return <CMSHomeworkEdit navigate={navigateTo} />;
                    case 'stats': return <CMSStatsEdit navigate={navigateTo} />;
                    case 'notify': return <CMSNotifyEdit navigate={navigateTo} />;
                    case 'social': return <CMSSocialUpdatesEdit navigate={navigateTo} />;
                    default: return <CMSDashboard navigate={navigateTo} />;
                }
            } else {
                return <CMSLogin />;
            }
        }
        return <Website navigate={navigateTo} content={content} />;
    };
    
    return <div className="font-sans antialiased">{renderPage()}</div>;
}

// ─── Student Portal (/student) ───────────────────────────────────────────────
const StudentPortal = () => {
    const [notices, setNotices] = useState({});
    const [homework, setHomework] = useState({});
    const [logoUrl, setLogoUrl] = useState(null);
    const [schoolName, setSchoolName] = useState('Shaleen Vidyalaya');
    const [loading, setLoading] = useState(true);
    const [selectedClass, setSelectedClass] = useState(() => window.localStorage.getItem('selectedClass') || null);
    const [showSelector, setShowSelector] = useState(false);
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'notices');
    const [notifStatus, setNotifStatus] = useState(() => {
        if (!('Notification' in window)) return 'dismissed';
        if (Notification.permission === 'granted') return 'subscribed';
        if (Notification.permission === 'denied') return 'denied';
        return null;
    });
    const [installPrompt, setInstallPrompt] = useState(null);
    const [showInstallModal, setShowInstallModal] = useState(false);
    const [general, setGeneral] = useState({});
    const [social, setSocial] = useState({});
    const [showSocialModal, setShowSocialModal] = useState(false);
    const socialModalShown = useRef(false);

    useEffect(() => {
        if (!selectedClass) setTimeout(() => setShowSelector(true), 400);

        // Capture install prompt and auto-show on student page arrival
        const isPwa = window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone;
        if (!isPwa && !window.sessionStorage.getItem('installDismissed')) {
            const handler = (e) => {
                e.preventDefault();
                setInstallPrompt(e);
                setShowInstallModal(true);
            };
            window.addEventListener('beforeinstallprompt', handler);
            return () => window.removeEventListener('beforeinstallprompt', handler);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'websiteContent', 'main'), (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                setNotices(d.notices || {});
                setHomework(d.homework || {});
                setLogoUrl(d.general?.logoUrl || null);
                setSchoolName(d.general?.schoolName || 'Shaleen Vidyalaya');
                setGeneral(d.general || {});
                const s = d.social || {};
                setSocial(s);
                if (!socialModalShown.current) {
                    const platforms = [
                        { key: 'facebook', ts: s.facebookNew },
                        { key: 'instagram', ts: s.instagramNew },
                        { key: 'youtube', ts: s.youtubeNew },
                    ];
                    const hasAnyNew = platforms.some(({ key, ts }) => ts && ts !== window.localStorage.getItem(`sv_seen_${key}`));
                    if (hasAnyNew) {
                        socialModalShown.current = true;
                        setShowSocialModal(true);
                    } else {
                        socialModalShown.current = true;
                    }
                }
            }
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleSelectClass = (cls) => {
        setSelectedClass(cls);
        try { window.localStorage.setItem('selectedClass', cls); } catch (_) {}
        updateSubscriptionClass(cls);
        setShowSelector(false);
    };

    const handleEnableNotifications = async () => {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') { setNotifStatus('denied'); return; }
        const sub = await registerAndSubscribe();
        if (sub) {
            try {
                await fetch('/api/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subscription: sub, selectedClass }),
                });
            } catch (_) {}
        }
        setNotifStatus('subscribed');
    };

    const commonNotices = Array.isArray(notices?.common) ? notices.common : [];
    const classNotices = selectedClass && Array.isArray(notices?.[selectedClass]) ? notices[selectedClass] : [];
    const classHomework = selectedClass && Array.isArray(homework?.[selectedClass]) ? homework[selectedClass] : [];

    const noticesToShow = [...commonNotices.map(n => ({ ...n, _scope: 'common' })), ...classNotices.map(n => ({ ...n, _scope: selectedClass }))]
        .sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));

    const homeworkToShow = [...classHomework].sort((a, b) => new Date(a?.dueDate || 0) - new Date(b?.dueDate || 0));

    if (loading) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-[#1e40af] border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">

            {/* App header */}
            <header className="bg-[#1e40af] text-white px-4 py-3 shadow-lg flex-shrink-0">
                <div className="max-w-lg mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {logoUrl
                            ? <img src={logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover shadow-sm" />
                            : <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center font-extrabold text-lg">S</div>
                        }
                        <div>
                            <p className="font-bold text-[15px] leading-tight">Student Portal</p>
                            <p className="text-[11px] text-white/65 leading-tight">{schoolName}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowSelector(true)}
                        className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                    >
                        {selectedClass || 'Select Class'}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>
            </header>

            {/* Notification prompt */}
            {notifStatus === null && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex-shrink-0">
                    <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
                        <p className="text-sm text-amber-800 font-medium">Get notified when new notices are posted</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={handleEnableNotifications} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors">Enable</button>
                            <button onClick={() => setNotifStatus('dismissed')} className="text-amber-500 text-xs hover:text-amber-700">Later</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Social links bar */}
            {(general.facebookUrl || general.instagramUrl || general.youtubeUrl) && (() => {
                const platforms = [
                    { key: 'facebook', url: general.facebookUrl, ts: social.facebookNew, label: 'Facebook', color: 'text-blue-600', icon: <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" /> },
                    { key: 'instagram', url: general.instagramUrl, ts: social.instagramNew, label: 'Instagram', color: 'text-pink-500', icon: <><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></> },
                    { key: 'youtube', url: general.youtubeUrl, ts: social.youtubeNew, label: 'YouTube', color: 'text-red-500', icon: <><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" /></> },
                ].filter(p => p.url);
                return (
                    <div className="bg-white border-b border-gray-100 px-4 py-2.5 flex-shrink-0">
                        <div className="max-w-lg mx-auto flex items-center gap-3">
                            <span className="text-xs text-[#6b7280] font-medium mr-1">Follow us:</span>
                            {platforms.map(({ key, url, ts, label, icon, color }) => {
                                const lsKey = `sv_seen_${key}`;
                                const seen = window.localStorage.getItem(lsKey);
                                const hasNew = ts && ts !== seen;
                                const handleClick = () => {
                                    if (ts) { try { window.localStorage.setItem(lsKey, ts); } catch (_) {} }
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                };
                                return (
                                    <button key={key} onClick={handleClick} aria-label={label}
                                        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors">
                                        <svg className={`w-5 h-5 ${color}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">{icon}</svg>
                                        {hasNew && <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10 flex-shrink-0">
                <div className="max-w-lg mx-auto flex">
                    {[
                        { key: 'notices', label: 'Notices', count: noticesToShow.length },
                        { key: 'homework', label: 'Homework', count: homeworkToShow.length },
                    ].map(({ key, label, count }) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`flex-1 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                                activeTab === key ? 'border-[#1e40af] text-[#1e40af]' : 'border-transparent text-[#6b7280] hover:text-[#111827]'
                            }`}
                        >
                            {label}
                            <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                activeTab === key ? 'bg-[#1e40af] text-white' : 'bg-gray-100 text-gray-500'
                            }`}>{count}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 px-4 py-4 max-w-lg mx-auto w-full">
                {activeTab === 'notices' ? (
                    <div className="space-y-3">
                        {noticesToShow.length === 0 ? (
                            <div className="text-center py-16 text-[#9ca3af]">
                                <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <p className="text-sm font-medium">No notices right now</p>
                                {!selectedClass && <p className="text-xs mt-1 text-[#9ca3af]">Select your class to see class notices too</p>}
                            </div>
                        ) : (
                            noticesToShow.map(n => <NoticeCard key={n.id || `${n._scope}-${n.title}`} notice={n} scope={n._scope} />)
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {!selectedClass ? (
                            <div className="text-center py-16 text-[#9ca3af]">
                                <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                <p className="text-sm font-medium mb-3">Select your class to see homework</p>
                                <button onClick={() => setShowSelector(true)} className="bg-[#1e40af] text-white text-sm font-semibold px-4 py-2 rounded-lg">Select Class</button>
                            </div>
                        ) : homeworkToShow.length === 0 ? (
                            <div className="text-center py-16 text-[#9ca3af]">
                                <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-sm font-medium">No homework for {selectedClass}</p>
                            </div>
                        ) : (
                            homeworkToShow.map(hw => <HomeworkCard key={hw.id || `${hw.subject}-${hw.dueDate}`} item={hw} />)
                        )}
                    </div>
                )}
            </main>

            <div className="py-4 text-center border-t border-gray-100 flex-shrink-0">
                <a href="/" className="text-xs text-[#9ca3af] hover:text-[#6b7280] transition-colors">← Back to school website</a>
            </div>

            {showSelector && (
                <ClassSelectorModal
                    onSelect={handleSelectClass}
                    onClose={selectedClass ? () => setShowSelector(false) : null}
                    current={selectedClass}
                />
            )}

            {/* Social updates modal — auto-pops if any platform has new post */}
            {showSocialModal && (() => {
                const platforms = [
                    { key: 'facebook', url: general.facebookUrl, ts: social.facebookNew, label: 'Facebook', color: 'text-blue-600', bg: 'bg-blue-50 hover:bg-blue-100', icon: <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" /> },
                    { key: 'instagram', url: general.instagramUrl, ts: social.instagramNew, label: 'Instagram', color: 'text-pink-500', bg: 'bg-pink-50 hover:bg-pink-100', icon: <><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></> },
                    { key: 'youtube', url: general.youtubeUrl, ts: social.youtubeNew, label: 'YouTube', color: 'text-red-500', bg: 'bg-red-50 hover:bg-red-100', icon: <><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" /></> },
                ].filter(({ url, ts, key }) => url && ts && ts !== window.localStorage.getItem(`sv_seen_${key}`));
                return (
                    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                            <div className="flex items-center justify-between mb-1">
                                <p className="font-extrabold text-[#111827] text-lg">New Updates!</p>
                                <button onClick={() => setShowSocialModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-sm text-[#6b7280] mb-4">Your school posted new content on social media.</p>
                            <div className="space-y-2 mb-5">
                                {platforms.map(({ key, url, ts, label, color, bg, icon }) => (
                                    <button key={key} onClick={() => {
                                        try { window.localStorage.setItem(`sv_seen_${key}`, ts); } catch (_) {}
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                        setSocial(prev => ({ ...prev }));
                                        const remaining = platforms.filter(p => p.key !== key && p.ts && p.ts !== window.localStorage.getItem(`sv_seen_${p.key}`));
                                        if (remaining.length === 0) setShowSocialModal(false);
                                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl ${bg} transition-colors`}>
                                        <svg className={`w-5 h-5 flex-shrink-0 ${color}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">{icon}</svg>
                                        <span className={`font-semibold text-sm ${color}`}>Open {label}</span>
                                        <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => setShowSocialModal(false)} className="w-full text-[#6b7280] text-sm py-2 hover:text-[#111827] transition-colors">
                                Later
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Install modal — auto-pops on arrival */}
            {showInstallModal && installPrompt && (
                <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center gap-4 mb-4">
                            <img src={logoUrl || '/logo192.png'} alt="" className="w-14 h-14 rounded-2xl object-cover shadow-sm" />
                            <div>
                                <p className="font-extrabold text-[#111827] text-lg leading-tight">Install Student App</p>
                                <p className="text-sm text-[#6b7280] mt-0.5">Shaleen Vidyalaya</p>
                            </div>
                        </div>
                        <p className="text-sm text-[#6b7280] mb-5">Add to your home screen for quick access to notices, homework, and class updates.</p>
                        <button
                            onClick={async () => {
                                installPrompt.prompt();
                                await installPrompt.userChoice;
                                setShowInstallModal(false);
                                setInstallPrompt(null);
                            }}
                            className="w-full bg-[#1e40af] hover:bg-blue-700 text-white font-bold py-3 rounded-xl text-sm transition-colors mb-2"
                        >
                            Install App
                        </button>
                        <button
                            onClick={() => { window.sessionStorage.setItem('installDismissed', '1'); setShowInstallModal(false); }}
                            className="w-full text-[#6b7280] text-sm py-2 hover:text-[#111827] transition-colors"
                        >
                            Continue in browser
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function App() {
    if (window.location.pathname.startsWith('/student')) return <StudentPortal />;
    return <MainApp />;
}

// --- Notification Permission Banner ---
const NotificationBanner = () => {
    const [status, setStatus] = useState(null); // null | 'asking' | 'subscribed' | 'denied' | 'dismissed'

    useEffect(() => {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) { setStatus('dismissed'); return; }
        if (Notification.permission === 'granted') { setStatus('subscribed'); return; }
        if (Notification.permission === 'denied') { setStatus('denied'); return; }

        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        const delay = isStandalone ? 1500 : 5000;
        const timer = setTimeout(() => setStatus('asking'), delay);

        const onInstalled = () => { clearTimeout(timer); setStatus('asking'); };
        window.addEventListener('appinstalled', onInstalled);

        return () => { clearTimeout(timer); window.removeEventListener('appinstalled', onInstalled); };
    }, []);

    const handleEnable = useCallback(async () => {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') { setStatus('denied'); return; }
        const subscription = await registerAndSubscribe();
        if (subscription) {
            try {
                const selectedClass = window.localStorage.getItem('selectedClass') || null;
                await fetch('/api/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subscription, selectedClass }),
                });
            } catch (err) {
                console.error('Failed to save subscription:', err);
            }
        }
        setStatus('subscribed');
    }, []);

    if (status !== 'asking') return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-primary)] text-white px-4 py-3 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="text-sm">Get notified about school announcements and events.</span>
            </div>
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button onClick={handleEnable} className="bg-white text-[var(--color-primary)] text-sm font-semibold px-3 py-1.5 rounded hover:bg-gray-100 transition-colors">
                    Enable
                </button>
                <button onClick={() => setStatus('dismissed')} className="text-white/70 hover:text-white text-xl leading-none">&times;</button>
            </div>
        </div>
    );
};

// --- PWA Install Banner ---
// --- Section Header (Reusable) ---
const SectionHeader = ({ label, heading, description, align = 'left' }) => (
    <div className={`mb-14 max-w-2xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1e40af] mb-3">
            {label}
        </p>
        <h2 className="text-[2.25rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold leading-[1.1] tracking-tight text-[#111827]">
            {heading}
        </h2>
        <div className={`mt-5 h-[2px] w-12 bg-[#1e40af] ${align === 'center' ? 'mx-auto' : ''}`} />
        {description && (
            <p className="mt-6 text-base text-[#6b7280] leading-relaxed">{description}</p>
        )}
    </div>
);

// --- Stats Bar ---
const DEFAULT_STATS = [
    { value: '2000+', label: 'Happy Students' },
    { value: '25+', label: 'Years of Excellence' },
    { value: '100%', label: 'Board Results' },
    { value: '#1', label: 'School in Naroda' },
];

const StatsBar = ({ stats }) => {
    const items = (stats && stats.length > 0) ? stats : DEFAULT_STATS;
    return (
        <div className="relative -mt-16 md:-mt-20 z-20">
            <div className="container mx-auto px-6">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 px-6 py-10 md:px-12 md:py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {items.map(({ value, label }, i) => (
                            <div key={i} className={`text-center md:text-left ${i > 0 ? 'md:border-l md:border-gray-100 md:pl-8' : ''}`}>
                                <div className="text-4xl md:text-5xl font-extrabold text-[#1e40af] tracking-tight leading-none">{value}</div>
                                <div className="text-sm text-[#6b7280] mt-3 font-medium">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- WhatsApp Floating Button ---
const WhatsAppButton = ({ number }) => {
    if (!number) return null;
    const cleaned = number.replace(/\D/g, '');
    return (
        <a href={`https://wa.me/${cleaned}`} target="_blank" rel="noopener noreferrer"
            className="fixed bottom-36 right-4 z-40 bg-green-500 hover:bg-green-600 text-white w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-colors duration-200"
            aria-label="Chat on WhatsApp">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.118 1.531 5.845L.057 23.569a.75.75 0 00.921.921l5.733-1.474A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.852 0-3.587-.5-5.082-1.375l-.364-.215-3.766.968.993-3.656-.235-.376A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
            </svg>
        </a>
    );
};

// --- Website Components ---
const Website = ({ navigate, content }) => {
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [activeSection, setActiveSection] = useState('home');

    // Suppress Chrome's default install mini-bar on the main site
    useEffect(() => {
        const suppress = (e) => e.preventDefault();
        window.addEventListener('beforeinstallprompt', suppress);
        return () => window.removeEventListener('beforeinstallprompt', suppress);
    }, []);

    useEffect(() => {
        const ids = NAV_LINKS.map(l => l.id);
        const observers = ids.map(id => {
            const el = document.getElementById(id);
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
                { threshold: 0.35 }
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach(o => o?.disconnect());
    }, []);

    const images = content.gallery?.images || [];
    const openLightbox = (index) => { setLightboxIndex(index); setLightboxImage(images[index]?.url); };
    const closeLightbox = () => { setLightboxIndex(null); setLightboxImage(null); };
    const prevImage = () => { const i = (lightboxIndex - 1 + images.length) % images.length; setLightboxIndex(i); setLightboxImage(images[i]?.url); };
    const nextImage = () => { const i = (lightboxIndex + 1) % images.length; setLightboxIndex(i); setLightboxImage(images[i]?.url); };

    return (
        <div className="bg-white overflow-x-hidden">
            <SEO title="Shaleen Vidyalaya – Best Gujarati Medium School in Naroda, Ahmedabad" description="Shaleen Vidyalaya is a leading GSEB-affiliated Gujarati medium school in Naroda, Ahmedabad. Quality education from Nursery to Class 10 with expert faculty." />
            <FaviconSetter faviconUrl={content.general?.faviconUrl} />
            <ThemeSetter theme={content.general} />
            <NewsTicker content={content.news} />
            <Header navigate={navigate} content={content.general} activeSection={activeSection} />
            <main>
                <HeroSection content={content.hero} generalContent={content.general} />
                <StatsBar stats={content.stats} />
                <FadeSection><AnnouncementsSection content={content.announcements} /></FadeSection>
                <FadeSection><AboutUsSection content={content.about} /></FadeSection>
                <FadeSection><ProgramsSection content={content.programs} /></FadeSection>
                <FadeSection><GallerySection content={content.gallery} onImageClick={openLightbox} /></FadeSection>
                <FadeSection><ContactSection content={content.contact} /></FadeSection>
            </main>
            <Footer navigate={navigate} content={content.general} />
            {lightboxImage && (
                <Lightbox
                    image={lightboxImage}
                    caption={images[lightboxIndex]?.caption}
                    onClose={closeLightbox}
                    onPrev={images.length > 1 ? prevImage : null}
                    onNext={images.length > 1 ? nextImage : null}
                    current={lightboxIndex + 1}
                    total={images.length}
                />
            )}
            <WhatsAppButton number={content.general?.whatsappNumber} />
            <ScrollToTop />
            <NotificationBanner />
        </div>
    );
};

const NewsTicker = ({ content = { items: [] } }) => {
    if (!content?.items || content.items.length === 0) return null;
    const tickerItems = [...content.items, ...content.items];

    return (
        // Added w-full to ensure it respects screen width boundaries
        <div className="w-full bg-[var(--color-primary)] text-white text-sm overflow-hidden whitespace-nowrap"> 
            <style>
                {`@keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } } .ticker-content { animation: ticker 40s linear infinite; }`}
            </style>
            <div className="ticker-content inline-block py-2">
                {tickerItems.map((item, index) => (<span key={index} className="mx-8">{item}</span>))}
            </div>
        </div>
    );
};


const NAV_LINKS = [
    { id: 'home', label: 'Home' },
    { id: 'announcements', label: 'Announcements' },
    { id: 'about', label: 'About Us' },
    { id: 'programs', label: 'Academics' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'contact', label: 'Contact' },
];

const Header = ({ navigate, content = {}, activeSection = 'home' }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const handleNavClick = (id) => {
        setMenuOpen(false);
        if (id === 'home') { navigate('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
            <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <img src={content.logoUrl || "https://i.imgur.com/r631Y6h.png"} alt="Shaleen Vidyalaya Logo" className="h-11 w-11" />
                    <div className="leading-tight">
                        <h1 className="text-[1.05rem] font-extrabold text-[#111827] tracking-tight">Shaleen Vidyalaya</h1>
                        <p className="text-[10px] uppercase tracking-[0.15em] text-[#6b7280] font-semibold">GSEB · Naroda</p>
                    </div>
                </div>
                <nav className="hidden md:flex space-x-9 items-center">
                    {NAV_LINKS.map(({ id, label }) => (
                        <a key={id} href={`#${id}`}
                           onClick={(e) => { e.preventDefault(); handleNavClick(id); }}
                           className={`relative text-sm font-medium transition-colors duration-200 ${activeSection === id ? 'text-[#1e40af]' : 'text-[#6b7280] hover:text-[#111827]'}`}>
                            {label}
                            {activeSection === id && <span className="absolute -bottom-[6px] left-0 right-0 h-[2px] bg-[#1e40af] rounded-full" />}
                        </a>
                    ))}
                    <a href="/student" className="bg-[#1e40af] hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                        Student Portal
                    </a>
                </nav>
                <button onClick={() => setMenuOpen(o => !o)} className="md:hidden p-2 text-[#111827] rounded-lg hover:bg-gray-100 transition-colors" aria-label="Toggle menu">
                    {menuOpen
                        ? <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                    }
                </button>
            </div>
            {menuOpen && (
                <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
                    {NAV_LINKS.map(({ id, label }) => (
                        <a key={id} href={`#${id}`}
                           onClick={(e) => { e.preventDefault(); handleNavClick(id); }}
                           className={`block px-6 py-3.5 border-b border-gray-50 text-sm font-medium ${activeSection === id ? 'text-[#1e40af] bg-blue-50/50' : 'text-[#6b7280] hover:bg-gray-50'}`}>
                            {label}
                        </a>
                    ))}
                    <a href="/student" className="block px-6 py-3.5 text-sm font-semibold text-[#1e40af] bg-blue-50/50">
                        Student Portal →
                    </a>
                </div>
            )}
        </header>
    );
};

const HeroSection = ({ content = {}, generalContent = {} }) => {
    const whatsappNumber = generalContent.whatsappNumber?.replace(/\D/g, '');
    const whatsappLink = whatsappNumber ? `https://wa.me/${whatsappNumber}` : '#';

    return (
        <section id="home" className="relative text-white min-h-screen flex items-center overflow-hidden"
            style={{ backgroundImage: `url('${content.imageUrl || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop'}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0 bg-[rgba(15,23,42,0.7)]" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.75) 100%)' }} />

            <div className="container mx-auto px-6 pt-32 pb-40 md:pt-40 md:pb-48 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white/90 text-[11px] font-semibold uppercase tracking-[0.15em] px-4 py-2 rounded-full mb-8 backdrop-blur-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
                        Est. 1999 · GSEB Affiliated · Naroda, Ahmedabad
                    </span>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-7">
                        {content.heading || "Excellence in\nEducation"}
                    </h1>
                    <p className="text-base md:text-lg mb-12 max-w-xl mx-auto text-white/70 leading-relaxed">
                        {content.subheading || "Nurturing young minds for a bright future at Shaleen Vidyalaya, Naroda."}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <a href={whatsappLink} target={whatsappNumber ? "_blank" : "_self"} rel="noopener noreferrer"
                            className={`bg-[#f59e0b] text-[#111827] font-semibold py-4 px-8 rounded-xl hover:bg-[#fbbf24] hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300 text-sm tracking-wide inline-flex items-center gap-2 ${!whatsappNumber ? 'cursor-not-allowed opacity-70' : ''}`}
                            onClick={!whatsappNumber ? (e) => e.preventDefault() : undefined}>
                            {content.buttonText || "Enroll Now"}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </a>
                        <a href="#about" onClick={(e) => { e.preventDefault(); document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }); }}
                            className="border border-white/40 text-white font-semibold py-4 px-8 rounded-xl hover:bg-white/10 hover:border-white/60 transition-all duration-300 text-sm tracking-wide inline-flex items-center gap-2">
                            Learn More
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                        </a>
                    </div>
                </div>
            </div>

            {/* White wave divider for seamless overlap with next section */}
            <div className="absolute bottom-0 left-0 right-0 z-[5] pointer-events-none">
                <svg viewBox="0 0 1200 80" preserveAspectRatio="none" className="w-full h-16 md:h-20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,64 C200,0 400,128 600,64 C800,0 1000,128 1200,64 L1200,80 L0,80 Z" fill="#ffffff" />
                </svg>
            </div>
        </section>
    );
};

const AboutUsSection = ({ content = {} }) => {
    const achievements = [
        {
            title: content.achievement1Title || 'GSEB Affiliated School',
            desc: content.achievement1Desc || 'Recognized by the Gujarat Secondary and Higher Secondary Education Board (GSEB).',
        },
        {
            title: content.achievement2Title || 'Smart Classrooms',
            desc: content.achievement2Desc || 'Digital learning tools integrated across every grade.',
        },
        {
            title: content.achievement3Title || 'Expert Faculty',
            desc: content.achievement3Desc || 'Experienced teachers passionate about every student.',
        },
    ];

    return (
        <section id="about" className="py-24 bg-white">
            <div className="container mx-auto px-6">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
                    {/* Image with decorative rectangle */}
                    <div className="relative">
                        <div className="absolute -top-6 -right-6 w-3/4 h-3/4 bg-[#1e40af] rounded-2xl -z-0 hidden md:block" />
                        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#f59e0b]/15 rounded-2xl -z-0 hidden md:block" />
                        <div className="relative z-10">
                            <img
                                src={content.imageUrl || "https://i.imgur.com/W2l8k15.jpg"}
                                alt="School Building"
                                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5] md:aspect-[5/6]"
                            />
                        </div>
                    </div>

                    {/* Content */}
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1e40af] mb-3">
                            About Our School
                        </p>
                        <h2 className="text-[2.25rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold leading-[1.1] tracking-tight text-[#111827]">
                            {content.heading || "A Legacy of Excellence in Education"}
                        </h2>
                        <div className="mt-5 h-[2px] w-12 bg-[#1e40af]" />
                        <p className="mt-7 text-base text-[#6b7280] leading-relaxed">
                            {content.content || "Shaleen Vidyalaya, located in the heart of Naroda, Ahmedabad, has been a beacon of knowledge and character development for over two decades. Our mission is to provide a holistic education that fosters intellectual curiosity, critical thinking, and a love for learning."}
                        </p>

                        <ul className="mt-10 space-y-5">
                            {achievements.map((item) => (
                                <li key={item.title} className="flex items-start gap-4">
                                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mt-0.5">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                                    </span>
                                    <div>
                                        <h4 className="text-[1.05rem] font-bold text-[#111827] leading-snug">{item.title}</h4>
                                        <p className="text-sm text-[#6b7280] mt-1 leading-relaxed">{item.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- Updated Announcements Section with Swipe Gestures ---
const AnnouncementsSection = ({ content = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalRef = useRef(null); 
    const timeoutRef = useRef(null); 
    // Refs for swipe detection
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const carouselRef = useRef(null); // Ref for the carousel container

    const startInterval = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        intervalRef.current = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % (content.length || 1));
        }, 5000);
    }, [content.length]);

    const restartIntervalWithDelay = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            startInterval();
        }, 3000);
    }, [startInterval]);

    useEffect(() => {
        if (!content || content.length === 0) return;
        startInterval();
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [content, startInterval]); 

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + content.length) % content.length);
        restartIntervalWithDelay(); 
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % content.length);
        restartIntervalWithDelay(); 
    };
    
    const goToIndex = (index) => {
        setCurrentIndex(index);
        restartIntervalWithDelay(); 
    };

    // --- Swipe Handling Logic ---
    const handleTouchStart = (e) => {
        touchStartX.current = e.targetTouches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };

    const handleTouchEnd = () => {
        const threshold = 50; // Minimum swipe distance
        if (touchStartX.current - touchEndX.current > threshold) {
            // Swiped left
            goToNext();
        } else if (touchEndX.current - touchStartX.current > threshold) {
            // Swiped right
            goToPrevious();
        }
        // Reset touch points
        touchStartX.current = 0;
        touchEndX.current = 0;
    };
    // --- End Swipe Handling ---


    if (!content || content.length === 0) return null;

    return (
        <section id="announcements" className="py-24 bg-slate-50">
            <div className="container mx-auto px-6">
                <SectionHeader
                    label="Stay Informed"
                    heading="Latest Announcements"
                    description="Important updates, events, and news from Shaleen Vidyalaya."
                />

                {/* Carousel container */}
                <div
                    ref={carouselRef}
                    className="relative"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Main content box */}
                    <div className="relative overflow-hidden bg-white rounded-2xl shadow-md border border-gray-100 min-h-[360px] md:min-h-[340px]">
                        {content.map((announcement, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                            >
                                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-10 p-8 md:p-10 h-full">
                                    {announcement.imageUrl && (
                                        <div className="md:w-2/5 flex-shrink-0 w-full max-w-sm">
                                            <img
                                                src={announcement.imageUrl}
                                                alt={announcement.title || 'Announcement'}
                                                className="rounded-xl w-full h-48 md:h-56 object-cover shadow-md"
                                            />
                                        </div>
                                    )}
                                    <div className={`text-center md:text-left ${announcement.imageUrl ? 'md:w-3/5' : 'w-full'}`}>
                                        {announcement.date && (
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1e40af] mb-3">
                                                {formatDateBadge(announcement.date)}
                                            </p>
                                        )}
                                        <h3 className="text-xl md:text-2xl font-extrabold text-[#111827] mb-3 leading-tight tracking-tight">{announcement.title}</h3>
                                        <p className="text-[#6b7280] leading-relaxed text-[15px]">{announcement.text}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Previous Button - Hidden on mobile */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-5 bg-white border border-gray-200 text-[#1e40af] w-12 h-12 rounded-full z-20 hover:shadow-lg hover:border-[#1e40af] transition-all hidden md:flex items-center justify-center"
                        aria-label="Previous Announcement"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    {/* Next Button - Hidden on mobile */}
                    <button
                        onClick={goToNext}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-5 bg-white border border-gray-200 text-[#1e40af] w-12 h-12 rounded-full z-20 hover:shadow-lg hover:border-[#1e40af] transition-all hidden md:flex items-center justify-center"
                        aria-label="Next Announcement"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                    </button>

                    {/* Dot Indicators */}
                    <div className="flex justify-center gap-2 mt-8">
                        {content.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToIndex(index)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-[#1e40af]' : 'w-1.5 bg-gray-300 hover:bg-gray-400'}`}
                                aria-label={`Go to announcement ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};


// --- Class Selector / Notices & Homework ---
const CLASS_LIST = Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`);
const ClassSelectorModal = ({ onSelect, onClose, current }) => {
    return (
        <div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
            onClick={onClose || undefined}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 md:p-8 relative my-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 text-2xl leading-none flex items-center justify-center"
                        aria-label="Close"
                    >
                        &times;
                    </button>
                )}
                <h3 className="text-xl md:text-2xl font-extrabold text-[#111827] tracking-tight text-center mb-2">
                    Select Your Class
                </h3>
                <p className="text-sm text-[#6b7280] text-center mb-6">
                    We'll show you notices and homework that matter to you.
                </p>

                <div className="grid grid-cols-4 gap-2 md:gap-3 mb-4">
                    {CLASS_LIST.map((cls) => {
                        const active = current === cls;
                        return (
                            <button
                                key={cls}
                                onClick={() => onSelect(cls)}
                                className={`text-xs md:text-sm font-semibold py-3 rounded-lg border transition-all ${
                                    active
                                        ? 'bg-[#1e40af] text-white border-[#1e40af]'
                                        : 'bg-white text-[#111827] border-gray-200 hover:border-[#1e40af] hover:bg-blue-50'
                                }`}
                            >
                                {cls.replace('Class ', '')}
                            </button>
                        );
                    })}
                </div>

            </div>
        </div>
    );
};

const formatDateBadge = (value) => {
    if (!value) return '';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        const day = String(d.getUTCDate()).padStart(2, '0');
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const year = d.getUTCFullYear();
        return `${day}/${month}/${year}`;
    } catch (_) {
        return value;
    }
};

const deleteBlobFile = (url) => {
    if (!url || !url.includes('.blob.vercel-storage.com')) return;
    fetch('/api/delete-file', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) }).catch(() => {});
};

const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
        const d = new Date(dueDate);
        if (Number.isNaN(d.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d < today;
    } catch (_) {
        return false;
    }
};

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg|bmp|avif|tiff?)$/i;
const isImageUrl = (url, fileName) => {
    if (fileName && IMAGE_EXT.test(fileName)) return true;
    if (!url) return false;
    try { return IMAGE_EXT.test(new URL(url).pathname); } catch (_) { return IMAGE_EXT.test(url); }
};

// Shared attachment renderer — tries image first, falls back to download link on error
const AttachmentView = ({ fileUrl, fileName, link, linkLabel }) => {
    const [imgFailed, setImgFailed] = useState(false);
    const [imgExpanded, setImgExpanded] = useState(false);
    const tryAsImage = fileUrl && !imgFailed;
    const showAsFile = fileUrl && imgFailed;
    return (
        <>
            {tryAsImage && (
                <div className="mt-3">
                    <img
                        src={fileUrl}
                        alt={fileName || 'attachment'}
                        onError={() => setImgFailed(true)}
                        onClick={() => setImgExpanded(v => !v)}
                        className={`rounded-lg border border-gray-100 cursor-zoom-in object-cover transition-all duration-300 ${imgExpanded ? 'w-full max-h-none' : 'max-h-48 w-full object-top'}`}
                    />
                    {imgExpanded && <button onClick={() => setImgExpanded(false)} className="mt-1 text-xs text-[#6b7280] hover:text-[#111827]">Collapse</button>}
                </div>
            )}
            {showAsFile && (
                <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                    className="mt-3 flex items-center gap-2 text-sm font-medium text-[#1e40af] hover:underline">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {fileName || 'Download attachment'}
                </a>
            )}
            {link && (
                <a href={link} target="_blank" rel="noopener noreferrer"
                    className="mt-3 flex items-center gap-2 text-sm font-medium text-[#1e40af] hover:underline">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {linkLabel || link}
                </a>
            )}
        </>
    );
};

const NoticeCard = ({ notice, scope }) => {
    const [expanded, setExpanded] = useState(false);
    const text = notice.text || '';
    const isLong = text.length > 220;
    const displayText = !isLong || expanded ? text : `${text.slice(0, 220).trimEnd()}…`;

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <h4 className="text-base md:text-[1.05rem] font-bold text-[#111827] leading-snug tracking-tight">
                    {notice.title || 'Untitled Notice'}
                </h4>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {scope && (
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                            scope === 'common'
                                ? 'bg-blue-50 text-[#1e40af]'
                                : 'bg-emerald-50 text-emerald-700'
                        }`}>
                            {scope === 'common' ? 'All Classes' : scope}
                        </span>
                    )}
                    {notice.date && (
                        <span className="text-[11px] font-semibold text-[#6b7280] bg-gray-50 border border-gray-100 px-2 py-1 rounded">
                            {formatDateBadge(notice.date)}
                        </span>
                    )}
                </div>
            </div>
            {text && (
                <p className="text-sm text-[#6b7280] leading-relaxed whitespace-pre-line">{displayText}</p>
            )}
            {isLong && (
                <button onClick={() => setExpanded(v => !v)} className="mt-2 text-xs font-semibold text-[#1e40af] hover:underline">
                    {expanded ? 'Show less' : 'Read more'}
                </button>
            )}
            <AttachmentView fileUrl={notice.fileUrl} fileName={notice.fileName} link={notice.link} linkLabel={notice.linkLabel} />
        </div>
    );
};

const HomeworkCard = ({ item }) => {
    const overdue = isOverdue(item.dueDate);
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                {item.subject && (
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-blue-50 text-[#1e40af]">
                        {item.subject}
                    </span>
                )}
                {item.dueDate && (
                    <span className={`text-[11px] font-semibold px-2 py-1 rounded border ${
                        overdue
                            ? 'bg-red-50 text-red-600 border-red-100'
                            : 'bg-gray-50 text-[#6b7280] border-gray-100'
                    }`}>
                        {overdue ? 'Overdue · ' : 'Due · '}{formatDateBadge(item.dueDate)}
                    </span>
                )}
            </div>
            <p className="text-sm text-[#374151] leading-relaxed whitespace-pre-line">
                {item.description || 'No description provided.'}
            </p>
            <AttachmentView fileUrl={item.fileUrl} fileName={item.fileName} link={item.link} />
        </div>
    );
};


const ProgramCard = ({ title, description, icon, number }) => (
    <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden p-8">
        {/* Large light gray numeral */}
        <span className="absolute top-3 right-5 text-6xl font-extrabold text-gray-100 leading-none select-none pointer-events-none">
            {number}
        </span>
        <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <div className="text-[#1e40af] w-6 h-6 flex items-center justify-center">{icon}</div>
            </div>
            <h3 className="text-[1.15rem] font-bold text-[#111827] mb-3 leading-snug tracking-tight">{title}</h3>
            <p className="text-[#6b7280] text-[14.5px] leading-relaxed">{description}</p>
        </div>
    </div>
);

const ProgramsSection = ({ content = {} }) => (
    <section id="programs" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
            <SectionHeader
                label="Academics"
                heading={content.heading || "Our Academic Programs"}
                description="Tailored curricula at every stage of a student's journey, designed to nurture curiosity, character, and confidence."
            />
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                <ProgramCard
                    number="01"
                    title={content.program1Title || "Primary School (1–5)"}
                    description={content.program1Desc || "Building strong foundations in literacy, numeracy, and science with a focus on joyful, activity-based learning."}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>}
                />
                <ProgramCard
                    number="02"
                    title={content.program2Title || "Middle School (6–8)"}
                    description={content.program2Desc || "Encouraging critical thinking and exploration across a broad range of subjects to prepare students for higher studies."}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
                />
                <ProgramCard
                    number="03"
                    title={content.program3Title || "High School (9–12)"}
                    description={content.program3Desc || "Specialized streams in Science, Commerce, and Arts with focused career guidance and board exam preparation."}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>}
                />
            </div>
        </div>
    </section>
);

const GallerySection = ({ content = { images: [] }, onImageClick }) => {
    if (!content?.images || content.images.length === 0) return null;
    return (
        <section id="gallery" className="py-24 bg-white">
            <div className="container mx-auto px-6">
                <SectionHeader
                    label="Campus Life"
                    heading="A Glimpse into Our Gallery"
                    description="Snapshots of moments, milestones, and memories from across our campus."
                />
                <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
                    {content.images.map((image, index) => (
                        <div
                            key={index}
                            className="break-inside-avoid mb-4 rounded-xl overflow-hidden cursor-pointer group relative shadow-sm hover:shadow-lg transition-shadow duration-300"
                            onClick={() => onImageClick(index)}
                        >
                            <img
                                src={image.url}
                                alt={image.caption || `Gallery image ${index + 1}`}
                                className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                            />
                            {image.caption && (
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white text-xs px-3 py-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <span className="font-medium">{image.caption}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Lightbox = ({ image, caption, onClose, onPrev, onNext, current, total }) => {
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && onPrev) onPrev();
            if (e.key === 'ArrowRight' && onNext) onNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, onPrev, onNext]);

    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50" onClick={onClose}>
            <button className="absolute top-4 right-4 text-white/80 hover:text-white text-4xl leading-none z-10" onClick={onClose}>&times;</button>
            {total > 1 && <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">{current} / {total}</span>}
            {onPrev && (
                <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors z-10">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
            )}
            {onNext && (
                <button onClick={(e) => { e.stopPropagation(); onNext(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors z-10">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
            )}
            <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img src={image} alt={caption || "Enlarged view"} className="max-w-[90vw] max-h-[80vh] object-contain rounded" />
                {caption && <p className="text-white/80 text-sm mt-3 max-w-md text-center">{caption}</p>}
            </div>
        </div>
    );
};


const ContactSection = ({ content = {} }) => {
    const items = [
        {
            label: 'Visit Us',
            value: content.address || "Nikol Canal Road, Nava Naroda, Ahmedabad, Gujarat 382330",
            icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>),
        },
        {
            label: 'Call Us',
            value: content.phone || "+91 79 1234 5678",
            icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>),
        },
        {
            label: 'Email Us',
            value: content.email || "admissions@shaleenschool.in",
            icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>),
        },
    ];

    return (
        <section id="contact" className="py-24 bg-white">
            <div className="container mx-auto px-6">
                <SectionHeader
                    label="Get in Touch"
                    heading={content.heading || "Visit Our Campus"}
                    description="We'd love to meet you. Drop by during school hours, give us a call, or send us a message."
                />
                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Map */}
                    <div className="lg:col-span-3 rounded-2xl overflow-hidden shadow-md border border-gray-100 bg-white">
                        <iframe
                            src="https://maps.google.com/maps?q=Shaleen+Vidyalaya+Nikol+Canal+Road+Nava+Naroda+Ahmedabad+Gujarat&output=embed&z=16"
                            width="100%" height="500" style={{ border: 0, display: 'block' }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Shaleen Vidyalaya Location">
                        </iframe>
                    </div>
                    {/* Info Card */}
                    <div className="lg:col-span-2">
                        <div className="bg-slate-50 rounded-2xl border border-gray-100 p-8 md:p-10 h-full">
                            <h3 className="text-xl font-extrabold text-[#111827] tracking-tight mb-1">Contact Information</h3>
                            <p className="text-sm text-[#6b7280] mb-8">Reach out and we'll respond promptly.</p>
                            <ul className="space-y-6">
                                {items.map((item) => (
                                    <li key={item.label} className="flex items-start gap-4">
                                        <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-[#1e40af] flex items-center justify-center">
                                            {item.icon}
                                        </span>
                                        <div className="pt-1">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#1e40af] mb-1">{item.label}</p>
                                            <p className="text-[15px] text-[#111827] leading-relaxed font-medium">{item.value}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};


const Footer = ({ navigate, content = {} }) => (
    <footer className="bg-slate-900 text-gray-300 border-t-4 border-[#1e40af]">
        <div className="container mx-auto px-6 py-16">
            <div className="grid md:grid-cols-3 gap-12 mb-12">
                <div>
                    <div className="flex items-center gap-3 mb-5">
                        <img src={content.logoUrl || "https://i.imgur.com/r631Y6h.png"} alt="Logo" className="h-11 w-11 rounded-lg bg-white p-1" />
                        <span className="text-white font-extrabold text-lg tracking-tight">Shaleen Vidyalaya</span>
                    </div>
                    <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
                        Empowering students with quality education since 1999. GSEB affiliated school in Naroda, Ahmedabad.
                    </p>
                    <div className="flex gap-3 mt-6">
                        {content.facebookUrl && <a href={content.facebookUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-[#1e40af] hover:border-[#1e40af] flex items-center justify-center transition-all"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /></svg></a>}
                        {content.instagramUrl && <a href={content.instagramUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-[#1e40af] hover:border-[#1e40af] flex items-center justify-center transition-all"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.024.06 1.378.06 3.808s-.012 2.784-.06 3.808c-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.024.048-1.378.06-3.808.06s-2.784-.012-3.808-.06c-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.048-1.024-.06-1.378-.06-3.808s.012-2.784.06-3.808c.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 016.08 2.525c.636-.247 1.363-.416 2.427-.465C9.53 2.013 9.884 2 12.315 2zM12 7a5 5 0 100 10 5 5 0 000-10zm0 8a3 3 0 110-6 3 3 0 010 6zm6.406-11.845a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5z" clipRule="evenodd" /></svg></a>}
                        {content.youtubeUrl && <a href={content.youtubeUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-[#1e40af] hover:border-[#1e40af] flex items-center justify-center transition-all"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.78 22 12 22 12s0 3.22-.42 4.814a2.506 2.506 0 01-1.768 1.768c-1.594.42-7.812.42-7.812.42s-6.218 0-7.812-.42a2.506 2.506 0 01-1.768-1.768C2 15.22 2 12 2 12s0-3.22.42-4.814a2.506 2.506 0 011.768-1.768C5.782 5 12 5 12 5s6.218 0 7.812.418zM9.54 15.582V8.418l6.152 3.582-6.152 3.582z" clipRule="evenodd" /></svg></a>}
                    </div>
                </div>
                <div>
                    <h4 className="text-white font-semibold mb-5 text-[11px] uppercase tracking-[0.15em]">Quick Links</h4>
                    <ul className="space-y-3 text-sm">
                        {NAV_LINKS.map(({ id, label }) => (
                            <li key={id}>
                                <a
                                    href={`#${id}`}
                                    onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
                                    className="text-gray-400 hover:text-[#f59e0b] transition-colors inline-flex items-center gap-2 group"
                                >
                                    <span className="w-0 group-hover:w-3 h-px bg-[#f59e0b] transition-all duration-300" />
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h4 className="text-white font-semibold mb-5 text-[11px] uppercase tracking-[0.15em]">Contact</h4>
                    <ul className="space-y-4 text-sm text-gray-400">
                        <li className="flex gap-3">
                            <svg className="w-4 h-4 text-[#f59e0b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <span className="leading-relaxed">Nikol Canal Road, Nava Naroda, Ahmedabad, Gujarat 382330</span>
                        </li>
                        <li className="flex gap-3">
                            <svg className="w-4 h-4 text-[#f59e0b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            <span>+91 79 2281 8117</span>
                        </li>
                        <li className="flex gap-3">
                            <svg className="w-4 h-4 text-[#f59e0b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            <span>admissions@shaleenschool.in</span>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10 pt-7 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-gray-500">
                <p>&copy; {new Date().getFullYear()} Shaleen Vidyalaya. All Rights Reserved.</p>
                <a href="#cms" onClick={(e) => { e.preventDefault(); navigate('cms'); }} className="hover:text-[#f59e0b] transition-colors">Admin CMS</a>
            </div>
        </div>
    </footer>
);

// --- CMS Components --- 
// (CMS components remain unchanged from the previous version, 
//  as the updates were only to the website's display logic. 
//  Provided here for completeness)
const CMSLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        try { await signInWithEmailAndPassword(auth, email, password); } catch (err) { setError(err.message); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
                <h2 className="text-2xl font-bold text-center mb-6">CMS Login</h2>
                <form onSubmit={handleLogin}>
                    <div className="mb-4"><label className="block text-gray-700">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded" required /></div>
                    <div className="mb-6"><label className="block text-gray-700">Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border rounded" required /></div>
                    {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">Login</button>
                </form>
            </div>
        </div>
    );
};

const CMSDashboard = ({ navigate }) => {
    const handleLogout = async () => { await signOut(auth); navigate('home'); };
    const sections = [
        { id: 'general', name: 'General & Theme', desc: 'Logo, favicon, colors, social links', color: 'bg-purple-50 border-purple-200', btn: 'bg-purple-600 hover:bg-purple-700' },
        { id: 'news', name: 'News Ticker', desc: 'Scrolling announcement bar at top', color: 'bg-yellow-50 border-yellow-200', btn: 'bg-yellow-600 hover:bg-yellow-700' },
        { id: 'hero', name: 'Hero Section', desc: 'Main banner heading & background image', color: 'bg-blue-50 border-blue-200', btn: 'bg-blue-600 hover:bg-blue-700' },
        { id: 'announcements', name: 'Announcements', desc: 'Carousel cards with titles & images', color: 'bg-orange-50 border-orange-200', btn: 'bg-orange-600 hover:bg-orange-700' },
        { id: 'about', name: 'About Us', desc: 'School description & photo', color: 'bg-green-50 border-green-200', btn: 'bg-green-600 hover:bg-green-700' },
        { id: 'programs', name: 'Programs', desc: 'Primary, Middle & High school cards', color: 'bg-teal-50 border-teal-200', btn: 'bg-teal-600 hover:bg-teal-700' },
        { id: 'gallery', name: 'Image Gallery', desc: 'Upload & manage school photos', color: 'bg-pink-50 border-pink-200', btn: 'bg-pink-600 hover:bg-pink-700' },
        { id: 'contact', name: 'Contact Info', desc: 'Address, phone & email', color: 'bg-indigo-50 border-indigo-200', btn: 'bg-indigo-600 hover:bg-indigo-700' },
        { id: 'stats', name: 'Stats Bar', desc: 'Edit homepage statistics (students, years, etc.)', color: 'bg-amber-50 border-amber-200', btn: 'bg-amber-600 hover:bg-amber-700' },
        { id: 'notices', name: 'Class Notices', desc: 'Manage class-wise notices', color: 'bg-cyan-50 border-cyan-200', btn: 'bg-cyan-600 hover:bg-cyan-700' },
        { id: 'homework', name: 'Homework', desc: 'Manage class-wise homework', color: 'bg-emerald-50 border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
        { id: 'notify', name: 'Send Notification', desc: 'Push alerts to all subscribers', color: 'bg-red-50 border-red-200', btn: 'bg-red-600 hover:bg-red-700' },
        { id: 'social', name: 'Social Updates', desc: 'Mark Facebook / Instagram / YouTube as having new content', color: 'bg-sky-50 border-sky-200', btn: 'bg-sky-600 hover:bg-sky-700' },
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Shaleen Vidyalaya CMS</h1>
                        <p className="text-xs text-gray-500">Content Management System</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('home')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">View Website</button>
                        <button onClick={handleLogout} className="text-sm bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600">Logout</button>
                    </div>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8">
                <p className="text-gray-500 mb-6 text-sm">Select a section to edit its content.</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {sections.map(section => (
                        <div key={section.id} className={`border rounded-lg p-5 hover:shadow-md transition-shadow ${section.color}`}>
                            <h2 className="text-lg font-bold text-gray-800 mb-1">{section.name}</h2>
                            <p className="text-xs text-gray-500 mb-4">{section.desc}</p>
                            <button onClick={() => navigate(`cms/${section.id}`)} className={`text-sm text-white py-1.5 px-4 rounded ${section.btn} transition-colors`}>
                                Edit
                            </button>
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

const CMSEdit = ({ sectionId, fields, title, navigate }) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    
    useEffect(() => {
        const docRef = doc(db, "websiteContent", "main");
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) setData(docSnap.data()[sectionId] || {});
            setLoading(false);
        });
        return () => unsubscribe();
    }, [sectionId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e, fieldName) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMessage('Uploading image...');
        try {
            const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
            if (!response.ok) throw new Error(`Upload failed: ${await response.text()}`);
            const newBlob = await response.json();
            setData(prev => ({ ...prev, [fieldName]: newBlob.url }));
            setMessage('Image uploaded successfully!');
        } catch (error) {
            console.error("Image upload error:", error);
            setMessage(`Error uploading image: ${error.message}`);
        } finally {
            setUploading(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            const docRef = doc(db, "websiteContent", "main");
            await setDoc(docRef, { [sectionId]: data }, { merge: true });
            setMessage('Content saved successfully!');
        } catch (error) {
            console.error("Error saving content: ", error);
            setMessage(`Error: ${error.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Content...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                        <p className="text-xs text-gray-400">CMS &rsaquo; {title}</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    {fields.map(field => {
                        const isImageUpload = ['logoUrl', 'faviconUrl', 'imageUrl'].includes(field);
                        const isTextArea = field.toLowerCase().includes('content') || field.toLowerCase().includes('desc');
                        const isColor = field.toLowerCase().includes('color');
                        
                        return (
                            <div key={field} className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2 capitalize">{field.replace(/([A-Z])/g, ' $1')}</label>
                                {isColor ? (
                                    <div className="flex items-center gap-2">
                                        <input type="color" name={field} value={data[field] || '#000000'} onChange={handleInputChange} className="w-12 h-10 p-1 border rounded" />
                                        <input type="text" name={field} value={data[field] || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded" placeholder="e.g. #2563EB" />
                                    </div>
                                ) : isImageUpload ? (
                                    <div>
                                        <input type="file" onChange={(e) => handleFileChange(e, field)} className="w-full px-3 py-2 border rounded mb-2" disabled={uploading} />
                                        {data[field] && <img src={data[field]} alt="preview" className="mt-2 h-40 object-cover rounded"/>}
                                    </div>
                                ) : isTextArea ? (
                                     <textarea name={field} value={data[field] || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded h-32" />
                                ) : (
                                    <input type="text" name={field} value={data[field] || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded" />
                                )}
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-4">
                        <button onClick={handleSave} disabled={saving || uploading} className="bg-green-500 text-white py-2 px-6 rounded hover:bg-green-600 disabled:bg-gray-400">
                            {saving ? 'Saving...' : (uploading ? 'Uploading...' : 'Save')}
                        </button>
                        {message && <p className={`text-sm ${message.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>{message}</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};

const CMSNewsTickerEdit = ({ navigate }) => {
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const docRef = doc(db, "websiteContent", "main");
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().news) setItems(docSnap.data().news.items || []);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    
    const handleAddItem = () => {
        if (newItem.trim() !== '') {
            setItems([...items, newItem.trim()]);
            setNewItem('');
        }
    };

    const handleRemoveItem = (indexToRemove) => setItems(items.filter((_, index) => index !== indexToRemove));
    
    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            const docRef = doc(db, "websiteContent", "main");
            await setDoc(docRef, { news: { items } }, { merge: true });
            setMessage('News ticker saved successfully!');
        } catch (error) {
            console.error("Error saving news ticker: ", error);
            setMessage(`Error: ${error.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Ticker...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Edit News Ticker</h1>
                        <p className="text-xs text-gray-400">CMS &rsaquo; News Ticker</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold mb-2">Current Items</h2>
                        <ul className="list-disc pl-5">
                            {items.map((item, index) => (
                                <li key={index} className="mb-2 flex justify-between items-center"><span>{item}</span><button onClick={() => handleRemoveItem(index)} className="text-red-500 hover:text-red-700 font-bold text-sm">Remove</button></li>
                            ))}
                        </ul>
                         {items.length === 0 && <p className="text-gray-500">No news items yet.</p>}
                    </div>
                    <div className="flex gap-2 mb-6">
                        <input type="text" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Enter new announcement" className="flex-grow px-3 py-2 border rounded" />
                        <button onClick={handleAddItem} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">Add Item</button>
                    </div>
                     <div className="flex items-center gap-4">
                        <button onClick={handleSave} disabled={saving} className="bg-green-500 text-white py-2 px-6 rounded hover:bg-green-600 disabled:bg-gray-400">
                            {saving ? 'Saving...' : 'Save All Changes'}
                        </button>
                        {message && <p className={`text-sm ${message.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>{message}</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};

const CMSGalleryEdit = ({ navigate }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [message, setMessage] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const inputFileRef = useRef(null);

    useEffect(() => {
        const docRef = doc(db, "websiteContent", "main");
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().gallery) setImages(docSnap.data().gallery.images || []);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setUploadProgress({ current: 0, total: fileArray.length });
        setMessage('');
        const newImages = [];
        for (let i = 0; i < fileArray.length; i++) {
            setUploadProgress({ current: i + 1, total: fileArray.length });
            try {
                const file = fileArray[i];
                const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
                if (!response.ok) throw new Error(`Upload failed: ${await response.text()}`);
                const newBlob = await response.json();
                newImages.push({ url: newBlob.url, caption: '' });
            } catch (error) {
                console.error("Image upload error:", error);
                setMessage(`Error uploading ${fileArray[i].name}: ${error.message}`);
            }
        }
        setImages(prev => [...prev, ...newImages]);
        setUploadProgress(null);
        if (inputFileRef.current) inputFileRef.current.value = '';
        if (newImages.length > 0) {
            setMessage(`${newImages.length} image(s) uploaded. Click "Save Gallery" to publish.`);
            setTimeout(() => setMessage(''), 5000);
        }
    };

    const handleFileChange = (e) => uploadFiles(e.target.files);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        uploadFiles(e.dataTransfer.files);
    };

    const handleRemoveImage = (indexToRemove) => setImages(images.filter((_, i) => i !== indexToRemove));

    const handleMoveImage = (index, direction) => {
        const swapIndex = index + direction;
        if (swapIndex < 0 || swapIndex >= images.length) return;
        const next = [...images];
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
        setImages(next);
    };

    const handleCaptionChange = (index, value) => {
        setImages(prev => prev.map((img, i) => i === index ? { ...img, caption: value } : img));
    };

    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            const docRef = doc(db, "websiteContent", "main");
            await setDoc(docRef, { gallery: { images } }, { merge: true });
            setMessage('Gallery saved successfully!');
        } catch (error) {
            console.error("Error saving gallery:", error);
            setMessage(`Error: ${error.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Gallery...</div>;

    const isUploading = uploadProgress !== null;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Edit Image Gallery</h1>
                        <p className="text-xs text-gray-400">CMS &rsaquo; Image Gallery</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 space-y-6">
                {/* Drop Zone */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-3">Upload Images</h2>
                    <div
                        className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'} ${isUploading ? 'cursor-not-allowed' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); if (!isUploading) setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                        onClick={() => !isUploading && inputFileRef.current?.click()}
                    >
                        {isUploading ? (
                            <div className="max-w-xs mx-auto">
                                <p className="text-blue-600 font-semibold mb-3">Uploading {uploadProgress.current} of {uploadProgress.total}...</p>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <p className="text-gray-600 font-medium">Drag & drop images here, or click to select</p>
                                <p className="text-gray-400 text-sm mt-1">You can select multiple files at once</p>
                            </>
                        )}
                        <input type="file" ref={inputFileRef} onChange={handleFileChange} multiple accept="image/*" className="hidden" disabled={isUploading} />
                    </div>
                    {message && (
                        <p className={`mt-3 text-sm font-medium ${message.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>{message}</p>
                    )}
                </div>

                {/* Image Grid */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Gallery Images ({images.length})</h2>
                        <button onClick={handleSave} disabled={saving || isUploading} className="bg-green-500 text-white py-2 px-6 rounded hover:bg-green-600 disabled:bg-gray-400 text-sm font-medium">
                            {saving ? 'Saving...' : 'Save Gallery'}
                        </button>
                    </div>
                    {images.length === 0 ? (
                        <p className="text-gray-400 text-center py-12">No images yet. Upload some above!</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {images.map((image, index) => (
                                <div key={index} className="border rounded-lg overflow-hidden bg-gray-50 flex flex-col">
                                    <div className="relative group">
                                        <img src={image.url} alt={image.caption || `Gallery item ${index + 1}`} className="w-full h-32 object-cover" />
                                        <button
                                            onClick={() => handleRemoveImage(index)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Remove"
                                        >&times;</button>
                                    </div>
                                    <div className="p-2 flex-grow flex flex-col gap-2">
                                        <input
                                            type="text"
                                            value={image.caption || ''}
                                            onChange={(e) => handleCaptionChange(index, e.target.value)}
                                            placeholder="Caption (optional)"
                                            className="w-full text-xs px-2 py-1 border rounded focus:outline-none focus:border-blue-400"
                                        />
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handleMoveImage(index, -1)}
                                                disabled={index === 0}
                                                className="flex-1 text-xs bg-gray-200 hover:bg-gray-300 disabled:opacity-30 rounded py-1 transition-colors"
                                                title="Move left"
                                            >&#8592;</button>
                                            <span className="text-xs text-gray-400 px-1">{index + 1}/{images.length}</span>
                                            <button
                                                onClick={() => handleMoveImage(index, 1)}
                                                disabled={index === images.length - 1}
                                                className="flex-1 text-xs bg-gray-200 hover:bg-gray-300 disabled:opacity-30 rounded py-1 transition-colors"
                                                title="Move right"
                                            >&#8594;</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

// --- CMS Announcements Edit Component (Unchanged) ---
const CMSAnnouncementsEdit = ({ navigate }) => {
    const [announcements, setAnnouncements] = useState([]);
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', text: '', imageUrl: '' });
    const [editingIndex, setEditingIndex] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        const docRef = doc(db, "websiteContent", "main");
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().announcements) {
                setAnnouncements(docSnap.data().announcements || []);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAnnouncement(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMessage('Uploading image...');
        try {
            const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
            if (!response.ok) throw new Error(`Upload failed: ${await response.text()}`);
            const newBlob = await response.json();
            setNewAnnouncement(prev => ({ ...prev, imageUrl: newBlob.url }));
            setMessage('Image uploaded successfully!');
        } catch (error) {
            console.error("Image upload error:", error);
            setMessage(`Error uploading image: ${error.message}`);
        } finally {
            setUploading(false);
             if (fileInputRef.current) fileInputRef.current.value = ""; 
            setTimeout(() => setMessage(''), 3000);
        }
    };

     const handleAddOrUpdateAnnouncement = () => {
        if (!newAnnouncement.title.trim() || !newAnnouncement.text.trim()) {
            setMessage('Title and Text are required.');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        let updatedAnnouncements;
        if (editingIndex !== null) {
            updatedAnnouncements = [...announcements];
            updatedAnnouncements[editingIndex] = newAnnouncement;
        } else {
            updatedAnnouncements = [...announcements, newAnnouncement];
        }
        
        setAnnouncements(updatedAnnouncements);
        setNewAnnouncement({ title: '', text: '', imageUrl: '' }); 
        setEditingIndex(null); 
        setMessage(editingIndex !== null ? 'Announcement updated locally.' : 'Announcement added locally.');
        setTimeout(() => setMessage(''), 3000);
    };

    const handleEdit = (index) => {
        setNewAnnouncement(announcements[index]);
        setEditingIndex(index);
    };
    
    const handleRemove = (indexToRemove) => {
        setAnnouncements(announcements.filter((_, index) => index !== indexToRemove));
        setMessage('Announcement removed locally.');
        setTimeout(() => setMessage(''), 3000);
    };

    const handleCancelEdit = () => {
        setNewAnnouncement({ title: '', text: '', imageUrl: '' });
        setEditingIndex(null);
    };

    const handleSaveAll = async () => {
        setSaving(true); setMessage('');
        try {
            const docRef = doc(db, "websiteContent", "main");
            await setDoc(docRef, { announcements }, { merge: true });
            setMessage('All announcements saved successfully!');
        } catch (error) {
            console.error("Error saving announcements: ", error);
            setMessage(`Error: ${error.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Announcements...</div>;

    return (
         <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Edit Announcements</h1>
                        <p className="text-xs text-gray-400">CMS &rsaquo; Announcements</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8">
                {/* Form for adding/editing */}
                <div className="bg-white p-8 rounded-lg shadow-md mb-8">
                    <h2 className="text-lg font-semibold mb-4">{editingIndex !== null ? 'Edit Announcement' : 'Add New Announcement'}</h2>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
                        <input type="text" name="title" value={newAnnouncement.title} onChange={handleInputChange} className="w-full px-3 py-2 border rounded" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Text</label>
                        <textarea name="text" value={newAnnouncement.text} onChange={handleInputChange} className="w-full px-3 py-2 border rounded h-24" required />
                    </div>
                     <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Image (Optional)</label>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="w-full px-3 py-2 border rounded mb-2" disabled={uploading} />
                        {newAnnouncement.imageUrl && <img src={newAnnouncement.imageUrl} alt="preview" className="mt-2 h-20 object-cover rounded"/>}
                    </div>
                    <div className="flex items-center gap-4">
                        <button onClick={handleAddOrUpdateAnnouncement} disabled={uploading} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 disabled:bg-gray-400">
                            {uploading ? 'Uploading...' : (editingIndex !== null ? 'Update Announcement' : 'Add Announcement')}
                        </button>
                        {editingIndex !== null && (
                            <button onClick={handleCancelEdit} className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400">
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </div>

                {/* List of current announcements */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                     <h2 className="text-lg font-semibold mb-4">Current Announcements</h2>
                     {announcements.length === 0 && <p className="text-gray-500">No announcements yet.</p>}
                     <div className="space-y-4">
                        {announcements.map((item, index) => (
                            <div key={index} className="border p-4 rounded-md flex justify-between items-start gap-4">
                                <div className="flex-grow">
                                    <h3 className="font-bold">{item.title}</h3>
                                    <p className="text-sm text-gray-600">{item.text.substring(0, 100)}{item.text.length > 100 ? '...' : ''}</p>
                                    {item.imageUrl && <img src={item.imageUrl} alt="" className="h-16 mt-2 rounded"/>}
                                </div>
                                <div className="flex-shrink-0 flex flex-col gap-2">
                                     <button onClick={() => handleEdit(index)} className="text-blue-500 hover:text-blue-700 text-sm font-medium">Edit</button>
                                    <button onClick={() => handleRemove(index)} className="text-red-500 hover:text-red-700 text-sm font-medium">Remove</button>
                                </div>
                            </div>
                        ))}
                     </div>
                      <div className="mt-8 flex items-center gap-4">
                        <button onClick={handleSaveAll} disabled={saving} className="bg-green-500 text-white py-2 px-6 rounded hover:bg-green-600 disabled:bg-gray-400">
                            {saving ? 'Saving...' : 'Save All Changes to Website'}
                        </button>
                        {message && <p className={`text-sm ${message.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>{message}</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};


// --- CMS: Class-wise Notices ---
const CMS_NOTICE_CLASS_OPTIONS = [
    { value: 'common', label: 'Common (All Classes)' },
    ...CLASS_LIST.map((c) => ({ value: c, label: c })),
];

const todayIso = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const CMSStatsEdit = ({ navigate }) => {
    const [stats, setStats] = useState(DEFAULT_STATS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const docRef = doc(db, 'websiteContent', 'main');
        const unsub = onSnapshot(docRef, (snap) => {
            if (snap.exists() && snap.data().stats) setStats(snap.data().stats);
            else setStats(DEFAULT_STATS);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleChange = (index, field, value) => {
        setStats(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            await setDoc(doc(db, 'websiteContent', 'main'), { stats }, { merge: true });
            setMessage('Stats saved!');
        } catch (e) {
            setMessage(`Error: ${e.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Edit Stats Bar</h1>
                        <p className="text-xs text-gray-400">CMS › Stats Bar</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 max-w-2xl">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <p className="text-sm text-gray-500 mb-6">Edit the 4 statistics shown on the homepage hero card.</p>
                    <div className="space-y-4">
                        {stats.map((stat, i) => (
                            <div key={i} className="flex gap-3 items-center border rounded-lg p-4 bg-gray-50">
                                <span className="text-xs text-gray-400 font-bold w-4">{i + 1}</span>
                                <div className="flex-1">
                                    <label className="text-xs text-gray-500 font-semibold block mb-1">Value</label>
                                    <input type="text" value={stat.value} onChange={e => handleChange(i, 'value', e.target.value)}
                                        className="w-full px-3 py-2 border rounded text-sm font-bold text-[#1e40af] focus:outline-none focus:border-blue-400" placeholder="e.g. 2000+" />
                                </div>
                                <div className="flex-1">
                                    <label className="text-xs text-gray-500 font-semibold block mb-1">Label</label>
                                    <input type="text" value={stat.label} onChange={e => handleChange(i, 'label', e.target.value)}
                                        className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-400" placeholder="e.g. Happy Students" />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 flex items-center gap-4">
                        <button onClick={handleSave} disabled={saving} className="bg-amber-600 text-white py-2 px-6 rounded hover:bg-amber-700 disabled:bg-gray-400 font-semibold">
                            {saving ? 'Saving...' : 'Save Stats'}
                        </button>
                        {message && <p className={`text-sm font-medium ${message.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>{message}</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};

const CMSNoticesEdit = ({ navigate }) => {
    const [allNotices, setAllNotices] = useState({});
    const [selectedClass, setSelectedClass] = useState('common');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [pendingNotify, setPendingNotify] = useState(null);
    const fileRef = useRef(null);
    const BLANK_NOTICE = { title: '', text: '', date: todayIso(), fileUrl: '', fileName: '', link: '', linkLabel: '' };
    const [form, setForm] = useState(BLANK_NOTICE);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'websiteContent', 'main'), (snap) => {
            setAllNotices(snap.exists() ? (snap.data().notices || {}) : {});
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const currentList = Array.isArray(allNotices[selectedClass]) ? allNotices[selectedClass] : [];

    const setList = (fn) => setAllNotices(prev => ({ ...prev, [selectedClass]: fn(Array.isArray(prev[selectedClass]) ? prev[selectedClass] : []) }));

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMessage('Uploading file...');
        try {
            const res = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.json();
            setForm(p => ({ ...p, fileUrl: blob.url, fileName: file.name }));
            setMessage('File uploaded.');
        } catch (err) { setMessage(`Upload error: ${err.message}`); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; setTimeout(() => setMessage(''), 3000); }
    };

    const handleSubmit = () => {
        if (!form.title.trim()) { setMessage('Title is required.'); setTimeout(() => setMessage(''), 3000); return; }
        if (!form.text.trim() && !form.fileUrl && !form.link) { setMessage('Add text, a file, or a link.'); setTimeout(() => setMessage(''), 3000); return; }
        if (editingId) {
            const existing = (allNotices[selectedClass] || []).find(n => n.id === editingId);
            if (existing?.fileUrl && existing.fileUrl !== form.fileUrl) deleteBlobFile(existing.fileUrl);
            setList(list => list.map(n => n.id === editingId ? { ...n, ...form } : n));
            setMessage('Notice updated locally. Save to publish.');
            setPendingNotify(null);
        } else {
            setList(list => [{ id: Date.now().toString(), ...form }, ...list]);
            setMessage('Notice added locally. Save to publish.');
            setPendingNotify({ title: form.title, body: form.text });
        }
        setForm(BLANK_NOTICE); setEditingId(null);
        setTimeout(() => setMessage(''), 3000);
    };

    const handleEdit = (item) => { setForm({ title: item.title, text: item.text || '', date: item.date || todayIso(), fileUrl: item.fileUrl || '', fileName: item.fileName || '', link: item.link || '', linkLabel: item.linkLabel || '' }); setEditingId(item.id); };
    const handleCancel = () => { setForm(BLANK_NOTICE); setEditingId(null); };
    const handleRemove = (id) => {
        const item = (allNotices[selectedClass] || []).find(n => n.id === id);
        if (item?.fileUrl) deleteBlobFile(item.fileUrl);
        setList(list => list.filter(n => n.id !== id));
        setPendingNotify(null);
    };

    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            await setDoc(doc(db, 'websiteContent', 'main'), { notices: { [selectedClass]: currentList } }, { merge: true });
            if (pendingNotify) {
                const classLabel = CMS_NOTICE_CLASS_OPTIONS.find(o => o.value === selectedClass)?.label || selectedClass;
                await fetch('/api/notify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: `📢 ${classLabel}: ${pendingNotify.title}`,
                        body: pendingNotify.body,
                        url: '/#notices',
                        targetClass: selectedClass,
                    }),
                }).catch(() => {});
                setPendingNotify(null);
            }
            setMessage('Saved!');
        } catch (e) { setMessage(`Error: ${e.message}`); }
        finally { setSaving(false); setTimeout(() => setMessage(''), 3000); }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div><h1 className="text-xl font-bold text-gray-800">Class Notices</h1><p className="text-xs text-gray-400">CMS &rsaquo; Class Notices</p></div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Class</label>
                    <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setForm(BLANK_NOTICE); setEditingId(null); }} className="w-full md:w-72 px-3 py-2 border rounded">
                        {CMS_NOTICE_CLASS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Notice' : 'Add Notice'}</h2>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Title *</label>
                        <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" placeholder="e.g. Annual Sports Day" />
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Text</label>
                        <textarea value={form.text} onChange={e => setForm(p => ({ ...p, text: e.target.value }))} className="w-full px-3 py-2 border rounded h-28 focus:outline-none focus:border-blue-400" placeholder="Notice details..." />
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Date</label>
                        <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="px-3 py-2 border rounded focus:outline-none focus:border-blue-400" />
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Link (optional)</label>
                        <div className="flex gap-2">
                            <input type="url" value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))} className="flex-1 px-3 py-2 border rounded focus:outline-none focus:border-blue-400" placeholder="https://..." />
                            <input type="text" value={form.linkLabel} onChange={e => setForm(p => ({ ...p, linkLabel: e.target.value }))} className="w-36 px-3 py-2 border rounded focus:outline-none focus:border-blue-400" placeholder="Label (optional)" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Attachment (optional)</label>
                        {form.fileUrl ? (
                            <div className="space-y-2">
                                {isImageUrl(form.fileUrl, form.fileName) && (
                                    <img src={form.fileUrl} alt={form.fileName} className="max-h-32 rounded border border-gray-200 object-contain" />
                                )}
                                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded px-3 py-2">
                                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                                    <a href={form.fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 font-medium flex-grow truncate hover:underline">{form.fileName || 'Attached file'}</a>
                                    <button onClick={() => setForm(p => ({ ...p, fileUrl: '', fileName: '' }))} className="text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <input type="file" ref={fileRef} onChange={handleFileUpload} disabled={uploading} className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button onClick={handleSubmit} className={`text-white py-2 px-5 rounded font-medium ${editingId ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'}`}>
                            {editingId ? 'Update Notice' : 'Add Notice'}
                        </button>
                        {editingId && <button onClick={handleCancel} className="bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300">Cancel</button>}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Notices — {CMS_NOTICE_CLASS_OPTIONS.find(o => o.value === selectedClass)?.label} <span className="text-sm text-gray-400">({currentList.length})</span></h2>
                        <button onClick={handleSave} disabled={saving} className="bg-green-500 text-white py-2 px-5 rounded hover:bg-green-600 disabled:bg-gray-400 text-sm font-medium">{saving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                    {currentList.length === 0 ? <p className="text-gray-400 text-center py-6">No notices yet.</p> : (
                        <ul className="space-y-3">
                            {currentList.map(n => (
                                <li key={n.id} className={`border rounded-lg p-4 ${editingId === n.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="font-semibold text-gray-800">{n.title}</span>
                                                {n.date && <span className="text-[11px] bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded">{formatDateBadge(n.date)}</span>}
                                                {n.fileUrl && <span className="text-[11px] bg-blue-50 text-blue-600 font-semibold px-2 py-0.5 rounded">{isImageUrl(n.fileUrl, n.fileName) ? '🖼 Image' : '📎 File'}</span>}
                                                {n.link && <span className="text-[11px] bg-purple-50 text-purple-600 font-semibold px-2 py-0.5 rounded">🔗 Link</span>}
                                            </div>
                                            {n.text && <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-2">{n.text}</p>}
                                            {isImageUrl(n.fileUrl, n.fileName) && <img src={n.fileUrl} alt={n.fileName} className="mt-2 max-h-20 rounded border border-gray-200 object-contain" />}
                                        </div>
                                        <div className="flex gap-2 flex-shrink-0">
                                            <button onClick={() => handleEdit(n)} className="text-blue-500 hover:text-blue-700 text-sm font-medium">Edit</button>
                                            <button onClick={() => handleRemove(n.id)} className="text-red-500 hover:text-red-700 text-sm font-medium">Remove</button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {message && <p className={`mt-4 text-sm font-medium ${message.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>{message}</p>}
                </div>
            </main>
        </div>
    );
};


// --- CMS: Class-wise Homework ---
// --- CMS: Class-wise Homework ---
const CMSHomeworkEdit = ({ navigate }) => {
    const [allHomework, setAllHomework] = useState({});
    const [selectedClass, setSelectedClass] = useState(CLASS_LIST[0]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [pendingNotify, setPendingNotify] = useState(null);
    const fileRef = useRef(null);
    const BLANK = { subject: '', description: '', dueDate: todayIso(), fileUrl: '', fileName: '', link: '' };
    const [form, setForm] = useState(BLANK);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'websiteContent', 'main'), (snap) => {
            setAllHomework(snap.exists() ? (snap.data().homework || {}) : {});
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const currentList = Array.isArray(allHomework[selectedClass]) ? allHomework[selectedClass] : [];
    const setList = (fn) => setAllHomework(prev => ({ ...prev, [selectedClass]: fn(Array.isArray(prev[selectedClass]) ? prev[selectedClass] : []) }));

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMessage('Uploading file...');
        try {
            const res = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.json();
            setForm(p => ({ ...p, fileUrl: blob.url, fileName: file.name }));
            setMessage('File uploaded.');
        } catch (err) { setMessage(`Upload error: ${err.message}`); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; setTimeout(() => setMessage(''), 3000); }
    };

    const handleSubmit = () => {
        if (!form.subject.trim() || !form.description.trim()) { setMessage('Subject and description required.'); setTimeout(() => setMessage(''), 3000); return; }
        const item = { ...form, subject: form.subject.trim(), description: form.description.trim() };
        if (editingId) {
            const existing = (allHomework[selectedClass] || []).find(h => h.id === editingId);
            if (existing?.fileUrl && existing.fileUrl !== item.fileUrl) deleteBlobFile(existing.fileUrl);
            setList(list => list.map(h => h.id === editingId ? { ...h, ...item } : h));
            setMessage('Updated locally. Save to publish.');
            setPendingNotify(null);
        } else {
            setList(list => [{ id: Date.now().toString(), ...item }, ...list]);
            setMessage('Added locally. Save to publish.');
            setPendingNotify({ subject: item.subject, description: item.description, dueDate: item.dueDate });
        }
        setForm(BLANK); setEditingId(null);
        setTimeout(() => setMessage(''), 3000);
    };

    const handleEdit = (item) => {
        setForm({ subject: item.subject, description: item.description, dueDate: item.dueDate || todayIso(), fileUrl: item.fileUrl || '', fileName: item.fileName || '', link: item.link || '' });
        setEditingId(item.id);
    };
    const handleCancel = () => { setForm(BLANK); setEditingId(null); };
    const handleRemove = (id) => {
        const item = (allHomework[selectedClass] || []).find(h => h.id === id);
        if (item?.fileUrl) deleteBlobFile(item.fileUrl);
        setList(list => list.filter(h => h.id !== id));
        setPendingNotify(null);
    };
    const handleClearFile = () => setForm(p => ({ ...p, fileUrl: '', fileName: '' }));

    const handleSave = async () => {
        setSaving(true); setMessage('');
        try {
            await setDoc(doc(db, 'websiteContent', 'main'), { homework: { [selectedClass]: currentList } }, { merge: true });
            if (pendingNotify) {
                const due = pendingNotify.dueDate ? ` · Due ${formatDateBadge(pendingNotify.dueDate)}` : '';
                await fetch('/api/notify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: `📚 ${selectedClass}: ${pendingNotify.subject}${due}`,
                        body: pendingNotify.description,
                        url: '/student?tab=homework',
                        targetClass: selectedClass,
                    }),
                }).catch(() => {});
                setPendingNotify(null);
            }
            setMessage('Saved!');
        } catch (e) { setMessage(`Error: ${e.message}`); }
        finally { setSaving(false); setTimeout(() => setMessage(''), 3000); }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div><h1 className="text-xl font-bold text-gray-800">Homework</h1><p className="text-xs text-gray-400">CMS &rsaquo; Homework</p></div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Class</label>
                    <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setForm(BLANK); setEditingId(null); }} className="w-full md:w-72 px-3 py-2 border rounded">
                        {CLASS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Homework' : 'Add Homework'}</h2>
                    <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Subject *</label>
                            <input type="text" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" placeholder="e.g. Mathematics" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Due Date</label>
                            <input type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Description *</label>
                        <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded h-24 focus:outline-none focus:border-blue-400" placeholder="What needs to be done..." />
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Link (optional)</label>
                        <input type="url" value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" placeholder="https://..." />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Attachment (optional)</label>
                        {form.fileUrl ? (
                            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded px-3 py-2">
                                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                                <a href={form.fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 font-medium flex-grow truncate hover:underline">{form.fileName || 'Attached file'}</a>
                                <button onClick={handleClearFile} className="text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                            </div>
                        ) : (
                            <div>
                                <input type="file" ref={fileRef} onChange={handleFileUpload} disabled={uploading} className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button onClick={handleSubmit} disabled={uploading} className={`text-white py-2 px-5 rounded font-medium disabled:bg-gray-400 ${editingId ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'}`}>
                            {editingId ? 'Update Homework' : 'Add Homework'}
                        </button>
                        {editingId && <button onClick={handleCancel} className="bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300">Cancel</button>}
                    </div>
                    {message && <p className={`mt-3 text-sm font-medium ${message.includes('Error') || message.includes('error') ? 'text-red-500' : 'text-blue-600'}`}>{message}</p>}
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Homework — {selectedClass} <span className="text-sm text-gray-400">({currentList.length})</span></h2>
                        <button onClick={handleSave} disabled={saving} className="bg-green-500 text-white py-2 px-5 rounded hover:bg-green-600 disabled:bg-gray-400 text-sm font-medium">{saving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                    {currentList.length === 0 ? <p className="text-gray-400 text-center py-6">No homework yet.</p> : (
                        <ul className="space-y-3">
                            {currentList.map(h => (
                                <li key={h.id} className={`border rounded-lg p-4 ${editingId === h.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-[#1e40af]">{h.subject}</span>
                                                {h.dueDate && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isOverdue(h.dueDate) ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'}`}>{isOverdue(h.dueDate) ? 'Overdue · ' : 'Due · '}{formatDateBadge(h.dueDate)}</span>}
                                            </div>
                                            <p className="text-sm text-gray-600 whitespace-pre-line mb-2">{h.description}</p>
                                            <div className="flex gap-3 flex-wrap">
                                                {h.fileUrl && <a href={h.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline font-medium"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>{h.fileName || 'Attachment'}</a>}
                                                {h.link && <a href={h.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-purple-600 hover:underline font-medium"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>Reference link</a>}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 flex-shrink-0">
                                            <button onClick={() => handleEdit(h)} className="text-blue-500 hover:text-blue-700 text-sm font-medium">Edit</button>
                                            <button onClick={() => handleRemove(h.id)} className="text-red-500 hover:text-red-700 text-sm font-medium">Remove</button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {message && <p className={`mt-4 text-sm font-medium ${message.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>{message}</p>}
                </div>
            </main>
        </div>
    );
};


const CMSNotifyEdit = ({ navigate }) => {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [url, setUrl] = useState('/');
    const [targetClass, setTargetClass] = useState('All');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const handleSend = async () => {
        if (!title.trim()) return;
        setSending(true); setResult(null);
        try {
            const res = await fetch('/api/notify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: title.trim(),
                    body: body.trim(),
                    url: url.trim() || '/',
                    targetClass,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to send');
            setResult({ success: true, message: `Sent to ${data.sent} device(s).${data.failed > 0 ? ` ${data.failed} failed.` : ''}` });
            if (data.sent > 0) { setTitle(''); setBody(''); setUrl('/'); setTargetClass('All'); }
        } catch (err) {
            setResult({ success: false, message: err.message });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Send Push Notification</h1>
                        <p className="text-xs text-gray-400">CMS &rsaquo; Notifications</p>
                    </div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 max-w-xl">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <p className="text-sm text-gray-500 mb-6">Sends a push notification. Select "All" to reach everyone, or pick a class to notify only that class.</p>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Title <span className="text-red-500">*</span></label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. School Closed Tomorrow" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" maxLength={100} />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Message</label>
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Optional message body..." className="w-full px-3 py-2 border rounded h-24 focus:outline-none focus:border-blue-400" maxLength={300} />
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Link (optional)</label>
                        <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400" />
                        <p className="text-xs text-gray-400 mt-1">Page to open when notification is tapped.</p>
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Target Class</label>
                        <select
                            value={targetClass}
                            onChange={(e) => setTargetClass(e.target.value)}
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-400"
                        >
                            <option value="All">All (Everyone)</option>
                            {CLASS_LIST.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-1">
                            Only students who selected this class will receive the notification.
                        </p>
                    </div>
                    {title && (
                        <div className="mb-6 border rounded-lg p-4 bg-gray-50">
                            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide font-medium">Preview</p>
                            <div className="flex items-start gap-3">
                                <img src="/logo192.png" alt="" className="w-10 h-10 rounded" />
                                <div>
                                    <p className="font-semibold text-sm text-gray-800">{title}</p>
                                    {body && <p className="text-xs text-gray-500 mt-0.5">{body}</p>}
                                    <p className="text-xs text-gray-400 mt-1">Shaleen Vidyalaya</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <button onClick={handleSend} disabled={sending || !title.trim()} className="w-full bg-red-600 text-white py-2.5 px-6 rounded hover:bg-red-700 disabled:bg-gray-400 font-semibold transition-colors">
                        {sending ? 'Sending...' : 'Send Notification'}
                    </button>
                    {result && (
                        <p className={`mt-4 text-sm font-medium text-center ${result.success ? 'text-green-600' : 'text-red-500'}`}>{result.message}</p>
                    )}
                </div>
            </main>
        </div>
    );
};

const CMSSocialUpdatesEdit = ({ navigate }) => {
    const [social, setSocial] = useState({ facebookNew: false, instagramNew: false, youtubeNew: false });
    const [general, setGeneral] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'websiteContent', 'main'), (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                setSocial(d.social || { facebookNew: false, instagramNew: false, youtubeNew: false });
                setGeneral(d.general || {});
            }
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const platforms = [
        { key: 'facebookNew', label: 'Facebook', url: general.facebookUrl },
        { key: 'instagramNew', label: 'Instagram', url: general.instagramUrl },
        { key: 'youtubeNew', label: 'YouTube', url: general.youtubeUrl },
    ];

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div><h1 className="text-xl font-bold text-gray-800">Social Updates</h1><p className="text-xs text-gray-400">CMS › Social Updates</p></div>
                    <button onClick={() => navigate('cms/dashboard')} className="text-sm text-gray-600 hover:text-blue-600 border border-gray-300 py-2 px-4 rounded hover:border-blue-400 transition-colors">Back to Dashboard</button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8 max-w-lg">
                <div className="bg-white p-6 rounded-lg shadow-md space-y-4">
                    <p className="text-sm text-gray-500">Tap "New Post" after posting on a platform. Students see a red dot — it disappears when they tap the icon.</p>
                    {platforms.map(({ key, label, url }) => {
                        const ts = social[key];
                        const isActive = !!ts;
                        const lastPosted = ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

                        const handleNewPost = async () => {
                            setSaving(true);
                            try {
                                await setDoc(doc(db, 'websiteContent', 'main'), { social: { [key]: new Date().toISOString() } }, { merge: true });
                                if (url) {
                                    await fetch('/api/notify', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            title: `📣 New ${label} Post`,
                                            body: `${general.schoolName || 'Shaleen Vidyalaya'} posted something new on ${label}. Tap to view.`,
                                            url,
                                            targetClass: 'All',
                                        }),
                                    }).catch(() => {});
                                }
                            } catch (e) { setMessage(`Error: ${e.message}`); }
                            finally { setSaving(false); }
                        };

                        const handleUndo = async () => {
                            setSaving(true);
                            try {
                                await setDoc(doc(db, 'websiteContent', 'main'), { social: { [key]: null } }, { merge: true });
                            } catch (e) { setMessage(`Error: ${e.message}`); }
                            finally { setSaving(false); }
                        };

                        return (
                            <div key={key} className="border border-gray-200 rounded-lg px-4 py-4 space-y-3">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-gray-800">{label}</p>
                                            {isActive && <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full">BADGE ON</span>}
                                        </div>
                                        {url
                                            ? <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate block max-w-xs">{url}</a>
                                            : <p className="text-xs text-gray-400">No URL — add in General Settings</p>
                                        }
                                    </div>
                                    <button onClick={handleNewPost} disabled={saving}
                                        className="flex-shrink-0 bg-[#1e40af] hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        New Post
                                    </button>
                                </div>
                                {lastPosted && (
                                    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                        <p className="text-xs text-gray-500">Last post: <span className="font-semibold text-gray-700">{lastPosted}</span></p>
                                        <button onClick={handleUndo} disabled={saving}
                                            className="text-xs text-red-500 hover:text-red-700 font-semibold ml-4 disabled:opacity-50">
                                            Undo
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {message && <p className="text-sm font-medium text-red-500">{message}</p>}
                </div>
            </main>
        </div>
    );
};
